import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ecoOpen, setEcoOpen] = useState(false);
  const ecoRef = useRef<HTMLDivElement>(null);
  const [mobileEcoOpen, setMobileEcoOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { openCart, totalCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ecoRef.current && !ecoRef.current.contains(e.target as Node)) {
        setEcoOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setEcoOpen(false);
    setMobileEcoOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Accueil', path: '/' },
    { label: 'Annuaire', path: '/directory' },
    { label: 'Catégories', path: '/categories' },
    { label: 'Culture', path: '/culture' },
    { label: 'Média', path: '/media' },
    { label: 'Annonces', path: '/annonces' },
    { label: 'WorldApeiron ADS', path: '/ads' },
    { label: 'Blog', path: '/blog' },
    { label: 'Tarifs', path: '/pricing' },
    { label: 'À propos', path: '/about' },
  ];

  const ecoModules = [
    { name: 'Shop', path: '/shop', icon: 'ri-shopping-bag-3-line', desc: 'Marketplace' },
    { name: 'Work', path: '/work', icon: 'ri-briefcase-4-line', desc: 'Freelance' },
    { name: 'Learn', path: '/learn', icon: 'ri-graduation-cap-line', desc: 'Formation' },
    { name: 'Jobs', path: '/jobs', icon: 'ri-id-card-line', desc: 'Emplois' },
    { name: 'Apartments', path: '/apartments', icon: 'ri-home-4-line', desc: 'Immobilier' },
    { name: 'Cloud', path: '/cloud', icon: 'ri-cloud-line', desc: 'Stockage' },
    { name: 'AI', path: '/ai', icon: 'ri-brain-line', desc: 'Intelligence' },
    { name: 'Pay', path: '/pay', icon: 'ri-wallet-3-line', desc: 'Paiements' },
    { name: 'Forum', path: '/forum', icon: 'ri-discuss-line', desc: 'Communauté' },
    { name: 'Média', path: '/media', icon: 'ri-film-line', desc: 'Vidéos & musique' },
    { name: 'Verify', path: '/verify', icon: 'ri-shield-check-line', desc: 'Vérification' },
    { name: 'Blog', path: '/blog', icon: 'ri-article-line', desc: 'Actualités' },
    { name: 'FAQ', path: '/faq', icon: 'ri-question-line', desc: 'Aide' },
    { name: 'Support', path: '/support', icon: 'ri-customer-service-2-line', desc: 'Assistance' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between min-h-16 md:min-h-20 h-auto py-2 md:py-2.5">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 md:gap-3 shrink-0">
            <img
              src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/52f74c8d2b3c4f047b22c4e6b94b8b4b.jpeg"
              alt="WorldApeiron"
              className="h-8 w-8 md:h-10 md:w-10 lg:h-12 lg:w-12 object-contain"
            />
            <span className="hidden lg:inline font-heading font-bold text-lg lg:text-xl text-foreground-50 whitespace-nowrap">
              World<span className="text-primary-400">Apeiron</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2 lg:gap-5 flex-wrap lg:flex-nowrap justify-center mx-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-xs lg:text-sm font-medium text-foreground-100 hover:text-primary-400 transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}

            {/* Ecosystem Dropdown */}
            <div ref={ecoRef} className="relative">
              <button
                onClick={() => setEcoOpen(!ecoOpen)}
                className="flex items-center gap-1 text-xs lg:text-sm font-medium text-foreground-100 hover:text-primary-400 transition-colors whitespace-nowrap"
              >
                Écosystème
                <i className={`ri-arrow-down-s-line transition-transform duration-300 ease-out ${ecoOpen ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[420px] lg:w-[560px] bg-background-50/98 backdrop-blur-xl border border-background-200/70 rounded-2xl shadow-2xl shadow-black/25 p-3 lg:p-4 grid grid-cols-2 gap-2 z-50 max-w-[90vw] transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${ecoOpen ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto' : 'opacity-0 -translate-y-3 scale-95 pointer-events-none'}`}
              >
                {ecoModules.map((mod, i) => (
                  <button
                    key={mod.name}
                    onClick={() => { navigate(mod.path); setEcoOpen(false); }}
                    className={`flex items-center gap-2 lg:gap-3 px-2.5 lg:px-3 py-2 lg:py-2.5 rounded-xl hover:bg-background-100 transition-all duration-300 text-left ${ecoOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}
                    style={{ transitionDelay: ecoOpen ? `${i * 40 + 80}ms` : '0ms' }}
                  >
                    <div className="w-8 h-8 lg:w-9 lg:h-9 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0 transition-transform duration-300 group-hover:scale-110">
                      <i className={`${mod.icon} text-base lg:text-lg text-primary-400`} />
                    </div>
                    <div>
                      <span className="block text-xs lg:text-sm font-semibold text-foreground-50">{mod.name}</span>
                      <span className="block text-[10px] lg:text-xs text-foreground-600">{mod.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3 shrink-0">
            <button
              onClick={() => navigate('/search')}
              className="w-8 h-8 lg:w-9 lg:h-9 flex items-center justify-center text-foreground-100 hover:text-primary-400 transition-colors"
              aria-label="Rechercher"
            >
              <i className="ri-search-line text-base lg:text-lg" />
            </button>
            <button
              onClick={openCart}
              className="relative w-8 h-8 lg:w-9 lg:h-9 flex items-center justify-center text-foreground-100 hover:text-primary-400 transition-colors"
              aria-label="Panier"
            >
              <i className="ri-shopping-cart-2-line text-base lg:text-lg" />
              {totalCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] lg:min-w-[18px] h-4 lg:h-[18px] px-0.5 lg:px-1 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 text-[9px] lg:text-[10px] font-bold">
                  {totalCount}
                </span>
              )}
            </button>
            {user && (
              <button
                onClick={() => navigate('/messages')}
                className="w-8 h-8 lg:w-9 lg:h-9 flex items-center justify-center text-foreground-100 hover:text-primary-400 transition-colors"
                aria-label="Messagerie"
              >
                <i className="ri-chat-3-line text-base lg:text-lg" />
              </button>
            )}
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="text-xs lg:text-sm font-medium text-foreground-100 hover:text-foreground-50 transition-colors whitespace-nowrap"
                >
                  Tableau de bord
                </Link>
                <button
                  onClick={() => { signOut(); navigate('/'); }}
                  className="px-3 py-1.5 lg:px-4 lg:py-2 text-xs lg:text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Se déconnecter
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/auth/login')}
                  className="text-xs lg:text-sm font-medium text-foreground-100 hover:text-foreground-50 transition-colors whitespace-nowrap"
                >
                  Connexion
                </button>
                <button
                  onClick={() => navigate('/auth/register')}
                  className="px-3 py-1.5 lg:px-4 lg:py-2 text-xs lg:text-sm font-semibold bg-primary-500 text-foreground-950 rounded-md hover:bg-primary-400 transition-colors whitespace-nowrap"
                >
                  Créer un compte
                </button>
              </>
            )}
          </div>

          {/* Mobile Cart + Dashboard + Hamburger */}
          <div className="flex items-center gap-1 md:hidden">
            {user && (
              <button
                onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
                className="relative w-10 h-10 flex items-center justify-center text-foreground-50 active:scale-90 transition-transform duration-150"
                aria-label="Tableau de bord"
              >
                <i className="ri-dashboard-line text-xl" />
              </button>
            )}
            <button
              onClick={openCart}
              className="relative w-10 h-10 flex items-center justify-center text-foreground-50 active:scale-90 transition-transform duration-150"
              aria-label="Panier"
            >
              <i className="ri-shopping-cart-2-line text-xl" />
              {totalCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 text-[10px] font-bold">
                  {totalCount}
                </span>
              )}
            </button>
            {user && (
              <button
                onClick={() => { navigate('/messages'); setMobileOpen(false); }}
                className="relative w-10 h-10 flex items-center justify-center text-foreground-50 active:scale-90 transition-transform duration-150"
                aria-label="Messagerie"
              >
                <i className="ri-chat-3-line text-xl" />
              </button>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center text-foreground-50 active:scale-90 transition-transform duration-150"
              aria-label="Menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${mobileOpen ? 'max-h-[90vh] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-background-50/98 backdrop-blur-md border-t border-background-200/70 max-h-[90vh] overflow-y-auto">
          <div className="px-4 py-6 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block text-base font-medium text-foreground-100 hover:text-primary-400 transition-colors"
              >
                {link.label}
              </Link>
            ))}

            <div className="pt-2">
              <button
                onClick={() => setMobileEcoOpen(!mobileEcoOpen)}
                className="flex items-center justify-between w-full text-xs font-semibold text-foreground-700 uppercase tracking-wider mb-2"
              >
                <span>Écosystème</span>
                <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-300 ${mobileEcoOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${mobileEcoOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <div className="grid grid-cols-2 gap-2 pb-1">
                  {ecoModules.map((mod, i) => (
                    <button
                      key={mod.name}
                      onClick={() => { navigate(mod.path); setMobileOpen(false); setMobileEcoOpen(false); }}
                      className={`flex items-center gap-2 lg:gap-3 px-3 py-2 lg:py-2.5 rounded-xl hover:bg-background-100 transition-all duration-300 text-left ${mobileEcoOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'}`}
                      style={{ transitionDelay: mobileEcoOpen ? `${i * 40 + 80}ms` : '0ms' }}
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0 transition-transform duration-300">
                        <i className={`${mod.icon} text-base text-primary-400`} />
                      </div>
                      <div>
                        <span className="block text-sm font-semibold text-foreground-50">{mod.name}</span>
                        <span className="block text-[10px] text-foreground-600">{mod.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-background-200/70 space-y-3">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 w-full text-left text-base font-medium text-primary-400"
                  >
                    <i className="ri-dashboard-line" />
                    Tableau de bord
                  </Link>
                  <button
                    onClick={() => { signOut(); setMobileOpen(false); navigate('/'); }}
                    className="block w-full px-4 py-2.5 text-center text-sm font-semibold border border-background-300/60 text-foreground-50 rounded-md"
                  >
                    Se déconnecter
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { navigate('/auth/login'); setMobileOpen(false); }}
                    className="block w-full text-left text-base font-medium text-foreground-100"
                  >
                    Connexion
                  </button>
                  <button
                    onClick={() => { navigate('/auth/register'); setMobileOpen(false); }}
                    className="block w-full px-4 py-2.5 text-center text-sm font-semibold bg-primary-500 text-foreground-950 rounded-md"
                  >
                    Créer un compte
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;